'use client';

import CartButton from '@/components/sections/builder/elements/header/header/cart-button';
import HeaderLogo from '@/components/sections/builder/elements/header/header/header-logo';
import HeaderMenuContent from '@/components/sections/builder/elements/header/header/header-menu-content';
import { HeaderElementDataType } from '@/components/sections/builder/elements/header/header/type';
import { ElementProps } from '@/components/sections/builder/types';
import { useLocationMultiOrigin, useMenu } from '@/hooks/api/storefront/menu-list';
import useModal from '@/hooks/useModal';
import useScrollDirection from '@/hooks/useScrollDirection';
import { cn } from '@/lib/utils';
import { IMenuItem } from '@/types/navigation';
import Cookies from 'js-cookie';
import { MapPin, Menu, Search, XIcon } from 'lucide-react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';

import SignInIcon from '@/components/icons/signin-icon';
import { UserNav } from '@/components/sections/builder/elements/header/header/user-nav';
import { UserNavMobile } from '@/components/sections/builder/elements/header/header/user-nav-mobile';
import ModalSearch from '@/components/sections/builder/search-modal/search-modal';
import { Input } from '@/components/ui/input';
import { ThemeType } from '@/stores/builder';
import clsx from 'clsx';
import { useCookies } from '@/app/cookie-provider';
import { deleteLocationMultiOrigin } from '@/lib/actions/cart/action';
import { DrawerSelectLocation } from '@/components/sections/builder/elements/body/drawer-select-location';
import { MultiOriginLocation, StoreOrderLocationPickup } from '@/types/http';

const SearchIconButton = ({ onClick }: { onClick: () => void }) => (
  <button onClick={onClick}>
    <Search className='h-6 w-6' />
  </button>
);

export default function HeaderContent({ element, preview, theme }: ElementProps) {
  const scrollDirection = useScrollDirection();
  const router = useRouter();
  const pathname = usePathname();
  const token = Cookies.get('accessToken');

  const query = useSearchParams();
  const themeId = query.get('theme_id') || '';
  const tokenPreview = query.get('token') || '';
  const { multiOrigin } = useCookies();

  const [headerFixed, setHeaderFixed] = useState<boolean>(false);
  const [headerStatic, setHeaderStatic] = useState<boolean>(true);
  const [collapsed, setCollapsed] = useState<boolean>(true);
  const [openMenuId, setOpenMenuId] = useState<string>('');
  const [openSubMenuId, setOpenSubMenuId] = useState<string>('');
  const [selectedLocation, setSelectedLocation] = useState<
    MultiOriginLocation | StoreOrderLocationPickup | undefined
  >();
  const [coordinates, setCoordinates] = useState<{ lat: number; long: number } | null>({
    lat: -6.216601629606373,
    long: 106.8182250518579,
  });

  const searchModal = useModal();
  const data = element.data as HeaderElementDataType;

  const header = typeof document !== 'undefined' ? document.getElementById('header-sticky') : null;
  const wrapperHeader =
    typeof document !== 'undefined'
      ? document.getElementById('wrapper-header')?.offsetHeight || 0
      : 0;

  const headerHeight = header?.offsetHeight || 0;
  const sticky = Number(header?.offsetTop);

  const modalMultiOrigin = useModal();

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleScroll = () => {
      const scrollOffset = window.pageYOffset;
      const spacing = data.spacing || 0;

      if (['on-scroll-up', 'always'].includes(data.stickyHeader as string)) {
        setHeaderFixed(
          data.stickyHeader === 'on-scroll-up' && scrollOffset > sticky + wrapperHeader - spacing
        );
        setHeaderStatic(scrollOffset <= sticky + (wrapperHeader - headerHeight) - spacing);
      }
    };

    if (['on-scroll-up', 'always'].includes(data.stickyHeader as string)) {
      window.addEventListener('scroll', handleScroll);
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [data.stickyHeader, data.spacing]);

  const { data: menuData } = useMenu(data.menuId, { enabled: !!data.menuId });
  const { data: locationData } = useLocationMultiOrigin({
    enabled: multiOrigin && !preview && pathname !== '/checkout',
  });

  const isMegaMenu =
    typeof menuData?.is_mega_menu === 'string'
      ? menuData.is_mega_menu === 'true'
      : !!menuData?.is_mega_menu;

  const menuItems = menuData?.menu_items || [];

  const selectedLocationId = Cookies.get('_location_id');
  const hasLocations = Array.isArray(locationData) && locationData.length > 0;
  const locationToDisplay = selectedLocation ?? (hasLocations ? locationData[0] : null);

  useEffect(() => {
    if (multiOrigin === false && selectedLocationId) {
      deleteLocationMultiOrigin();
    }
  }, [multiOrigin, selectedLocationId]);

  useEffect(() => {
    if (!selectedLocation && hasLocations && pathname !== '/checkout') {
      const fallback =
        locationData.find((loc) => loc.location_id === selectedLocationId) ||
        locationData.find((loc) => loc.is_default) ||
        locationData[0];

      setSelectedLocation(fallback);
    }
  }, [locationData, hasLocations]);

  return (
    <>
      <div
        className={cn(
          'fixed inset-0 z-[250]  block transform overflow-hidden bg-gray-900 bg-opacity-25 ease-in-out lg:hidden ',
          !collapsed
            ? '-translate-x-0 opacity-100 transition-opacity'
            : '-translate-x-full opacity-0 transition-all'
        )}
      >
        <div
          className={cn(
            'absolute left-0  h-full w-full transform bg-white text-left shadow-xl transition-all delay-200 duration-200 ease-in-out',
            !collapsed ? '-translate-x-0' : '-translate-x-full'
          )}
        >
          <div
            className={cn(
              'relative flex h-full w-full flex-col space-y-1 overflow-y-auto p-6',
              token ? 'pb-36' : 'pb-20'
            )}
          >
            <div className='mb-4 flex justify-between'>
              <HeaderLogo {...data} />
              <button type='button' onClick={() => setCollapsed(true)}>
                <XIcon className='h-6 w-6' />
              </button>
            </div>

            <nav aria-label='Header Navigation'>
              <ul>
                {menuItems.map((menu) => (
                  <li key={menu.id} className='flex flex-shrink-0 md:flex-wrap'>
                    <HeaderMenuContent
                      menu={menu as IMenuItem}
                      isMegaMenu={isMegaMenu}
                      openMenuId={openMenuId || ''}
                      setOpenMenuId={setOpenMenuId}
                      openSubMenuId={openSubMenuId || ''}
                      setOpenSubMenuId={setOpenSubMenuId}
                    />
                  </li>
                ))}
              </ul>
            </nav>
          </div>
          <UserNavMobile token={token} onClose={() => setCollapsed(true)} />
        </div>
        <div className='h-full w-full cursor-pointer' onClick={() => setCollapsed(true)}></div>
      </div>

      {/* Modal search bar */}
      {data.searchBarPosition === 'modal' ||
      data.searchBarPosition === '' ||
      data.searchBarPosition === undefined ? (
        <div
          className={cn(
            'mx-auto w-full',
            data.styleWidth === 'box' ? 'theme_layout_page_width' : 'header-full-width'
          )}
        >
          <header
            id='header-sticky'
            className={cn(
              'padding-header color-scheme-header inset-x-0 z-[100] w-full  px-6 py-4 text-sm md:py-6',
              headerFixed && 'offset-top fixed duration-300',
              (scrollDirection === 'up' || data.stickyHeader === 'always') &&
                !headerStatic &&
                'fixed !top-0 duration-300',
              ['top-center'].includes(data.logoPosition as string)
                ? 'justify-center md:justify-between'
                : 'justify-start md:justify-between',
              ['top-left', 'top-center'].includes(data.logoPosition as string)
                ? 'items-center md:items-start md:justify-between'
                : 'items-center',
              data.styleWidth !== 'box' && 'md:px-6'
            )}
          >
            <div
              className={cn(
                'mx-auto flex h-full w-full',
                data.styleWidth === 'box' ? 'theme_layout_page_width' : 'header-full-width'
              )}
            >
              {/* ignore */}
              <button
                type='button'
                onClick={() => setCollapsed((v) => !v)}
                className={cn(
                  'z-50 block pr-4 md:hidden',
                  ['top-center'].includes(data.logoPosition as string) && 'md:absolute md:left-6',
                  ['middle-center'].includes(data.logoPosition as string) &&
                    ' items-center  md:flex lg:hidden'
                )}
              >
                <Menu className='h-6 w-6' />
              </button>
              <div
                className={cn(
                  'hidden   md:flex md:gap-x-5',
                  data.logoPosition === 'top-left' && 'flex-col items-start gap-y-6 md:w-full',
                  data.logoPosition === 'top-center' &&
                    'flex-col items-center justify-center gap-y-6 md:w-full',
                  data.logoPosition === 'middle-left' && 'items-center md:w-full',
                  data.logoPosition === 'middle-center' && ' hidden w-full md:flex  '
                )}
              >
                <div
                  className={cn(
                    data.logoPosition !== 'middle-center' ? 'flex ' : 'hidden',
                    ['top-center', 'top-left'].includes(data?.logoPosition as string) &&
                      'w-full items-center justify-between '
                  )}
                >
                  {data.logoPosition === 'top-center' && (
                    <div className=''>
                      {data.showSearch && <SearchIconButton onClick={searchModal.open} />}
                    </div>
                  )}
                  <HeaderLogo {...data} />
                  {['top-center', 'top-left'].includes(data?.logoPosition as string) ? (
                    <div className='flex items-center gap-3' data-test-id='header-logo-center'>
                      {data.logoPosition !== 'top-center' && data.showSearch && (
                        <SearchIconButton onClick={searchModal.open} />
                      )}
                      {token ? (
                        <UserNav />
                      ) : (
                        <button
                          onClick={() =>
                            router.push(
                              `/account/signin${
                                tokenPreview ? `?theme_id=${themeId}&token=${tokenPreview}` : ''
                              }`
                            )
                          }
                        >
                          <SignInIcon className='h-7 w-7 hover:cursor-pointer' />
                        </button>
                      )}
                      <CartButton
                        host={typeof window !== 'undefined' ? window.location.host : ''}
                        preview={!!preview}
                        theme={theme as ThemeType}
                        parentData={data}
                      />
                    </div>
                  ) : null}
                </div>

                <nav aria-label='Header Navigation' className='flex items-center'>
                  <ul
                    className={cn(
                      ' gap-navigation hidden w-full  items-center justify-center gap-y-4 md:flex md:flex-wrap',
                      data.logoPosition === 'top-center' && 'md:justify-center',
                      data.logoPosition === 'top-left' && 'md:justify-start',
                      data.logoPosition === 'middle-left'
                        ? 'md:justify-start'
                        : 'md:justify-center',
                      data.logoPosition === 'middle-center' &&
                        ' hidden flex-1 justify-start md:hidden lg:flex'
                    )}
                  >
                    {menuItems.map((menu) => (
                      <li key={menu.id}>
                        <HeaderMenuContent
                          parentData={data}
                          isMegaMenu={isMegaMenu}
                          menu={menu as IMenuItem}
                          preview={preview}
                          openMenuId={openMenuId || ''}
                          setOpenMenuId={setOpenMenuId}
                          openSubMenuId={openSubMenuId || ''}
                          setOpenSubMenuId={setOpenSubMenuId}
                        />
                      </li>
                    ))}
                  </ul>
                </nav>

                <span
                  className={cn(
                    data.logoPosition === 'middle-center' ? 'flex flex-1   ' : 'hidden'
                  )}
                >
                  <div className='hidden flex-1 items-center justify-center md:flex'>
                    <HeaderLogo {...data} />
                  </div>
                  <div
                    className={cn(
                      data.logoPosition === 'middle-center'
                        ? '  flex   items-center justify-end gap-x-3   lg:hidden '
                        : 'hidden'
                    )}
                  >
                    {data.logoPosition !== 'top-center' && data.showSearch && (
                      <SearchIconButton onClick={searchModal.open} />
                    )}

                    {token ? (
                      <UserNav />
                    ) : (
                      <button
                        onClick={() =>
                          router.push(
                            `/account/signin${
                              tokenPreview ? `?theme_id=${themeId}&token=${tokenPreview}` : ''
                            }`
                          )
                        }
                      >
                        <SignInIcon className='h-7 w-7 hover:cursor-pointer' />
                      </button>
                    )}
                    <CartButton
                      host={typeof window !== 'undefined' ? window.location.host : ''}
                      preview={!!preview}
                      theme={theme as ThemeType}
                    />
                  </div>
                </span>
                <div
                  className={cn(
                    data.logoPosition === 'middle-center'
                      ? ' hidden  flex-1 items-center justify-end gap-x-3   lg:flex  '
                      : 'hidden'
                  )}
                >
                  {data.logoPosition !== 'top-center' && data.showSearch && (
                    <SearchIconButton onClick={searchModal.open} />
                  )}

                  {token ? (
                    <UserNav />
                  ) : (
                    <button
                      onClick={() =>
                        router.push(
                          `/account/signin${
                            tokenPreview ? `?theme_id=${themeId}&token=${tokenPreview}` : ''
                          }`
                        )
                      }
                    >
                      <SignInIcon className='h-7 w-7 hover:cursor-pointer' />
                    </button>
                  )}
                  <CartButton
                    host={typeof window !== 'undefined' ? window.location.host : ''}
                    preview={!!preview}
                    theme={theme as ThemeType}
                  />
                </div>
              </div>
              {/* ignore */}
              <div
                className={cn(
                  'flex w-full md:hidden',
                  data.mobileLogoPosition === 'left' && 'justify-start',
                  data.mobileLogoPosition === 'center' && '-ml-10 items-center justify-center'
                )}
              >
                <HeaderLogo {...data} />
              </div>
              {!['top-center', 'top-left', 'middle-center'].includes(
                data?.logoPosition as string
              ) ? (
                <div
                  className={cn('hidden min-w-[140px] items-center justify-end gap-x-4   md:flex')}
                >
                  {data.logoPosition !== 'top-center' && data.showSearch && (
                    <SearchIconButton onClick={searchModal.open} />
                  )}

                  {token ? (
                    <UserNav />
                  ) : (
                    <button
                      onClick={() =>
                        router.push(
                          `/account/signin${
                            tokenPreview ? `?theme_id=${themeId}&token=${tokenPreview}` : ''
                          }`
                        )
                      }
                    >
                      <SignInIcon className='h-7 w-7 hover:cursor-pointer' />
                    </button>
                  )}
                  <CartButton
                    host={typeof window !== 'undefined' ? window.location.host : ''}
                    preview={!!preview}
                    theme={theme as ThemeType}
                    parentData={data}
                  />
                </div>
              ) : null}
              <div
                className={cn(
                  'absolute right-6 z-40 flex items-center justify-center gap-x-3 self-center md:hidden  md:flex-row',
                  ['top-center'].includes(data.logoPosition as string) && 'absolute right-6'
                )}
              >
                <div className='mt-2'>
                  {data.showSearch && <SearchIconButton onClick={searchModal.open} />}
                </div>

                <CartButton
                  host={typeof window !== 'undefined' ? window.location.host : ''}
                  preview={!!preview}
                  theme={theme as ThemeType}
                  parentData={data}
                />
              </div>
            </div>
            {multiOrigin && (
              <div className='flex items-center justify-center gap-x-2 py-3'>
                <MapPin className='h-5 w-5' />
                <span>Delivery or Pickup from</span>
                <button
                  onClick={() => {
                    modalMultiOrigin.open();
                  }}
                  className='font-bold'
                >
                  {selectedLocation
                    ? selectedLocation.location_name
                    : locationToDisplay?.location_name}
                </button>
              </div>
            )}
          </header>
        </div>
      ) : (
        <div
          className={cn(
            'mx-auto w-full',
            data.styleWidth === 'box' ? 'theme_layout_page_width' : 'header-full-width'
          )}
        >
          <header
            id='header-sticky'
            className={cn(
              'padding-header color-scheme-header inset-x-0 z-[100] w-full px-4 py-4 text-sm',
              headerFixed && 'offset-top fixed duration-300',
              (scrollDirection === 'up' || data.stickyHeader === 'always') &&
                !headerStatic &&
                'fixed !top-0 duration-300',
              data.styleWidth !== 'box' && 'md:px-6'
            )}
          >
            <div
              className={cn(
                'mx-auto flex w-full items-center justify-between gap-x-2',
                data.styleWidth === 'box' ? 'theme_layout_page_width' : 'header-full-width'
              )}
            >
              <div className='flex w-fit flex-none items-center justify-between gap-3 gap-x-3 lg:flex-1 '>
                <HeaderLogo {...data} />

                <nav aria-label='Header Navigation'>
                  <ul
                    className={cn('gap-navigation hidden flex-1 items-center lg:flex lg:flex-wrap')}
                  >
                    {menuItems.map((menu) => (
                      <li key={menu.id}>
                        <HeaderMenuContent
                          parentData={data}
                          isMegaMenu={isMegaMenu}
                          menu={menu as IMenuItem}
                          preview={preview}
                          openMenuId={openMenuId || ''}
                          setOpenMenuId={setOpenMenuId}
                          openSubMenuId={openSubMenuId || ''}
                          setOpenSubMenuId={setOpenSubMenuId}
                        />
                      </li>
                    ))}
                  </ul>
                </nav>
              </div>
              <Input
                name='keyword'
                type='text'
                data-test-id='header-search-input'
                placeholder='Search...'
                className={clsx(
                  'theme_input w-full lg:w-[280px] lg:flex-none xl:w-[400px]',
                  data.logoPosition === 'middle-left' && 'lg:hidden'
                )}
                classNameWrapper='w-full lg:w-auto'
                onClick={searchModal.open}
                rightContent={
                  <button
                    type='button'
                    className={cn('', data.logoPosition === 'middle-left' && 'lg:hidden')}
                  >
                    <Search className='h-4 w-4' />
                  </button>
                }
              />

              <div className='flex w-fit flex-none items-center justify-end gap-x-4 lg:flex lg:flex-1'>
                {data.logoPosition === 'middle-left' && (
                  <Input
                    name='keyword'
                    type='text'
                    data-test-id='header-search-input'
                    placeholder='Search...'
                    className={clsx(
                      'theme_input w-full lg:w-[280px] lg:flex-none xl:w-[400px]',
                      data.logoPosition === 'middle-left' && 'hidden lg:flex'
                    )}
                    classNameWrapper='w-full lg:w-auto'
                    onClick={searchModal.open}
                    rightContent={
                      <button type='button' className={cn('hidden lg:flex')}>
                        <Search className='h-4 w-4' />
                      </button>
                    }
                  />
                )}
                <CartButton
                  host={typeof window !== 'undefined' ? window.location.host : ''}
                  preview={!!preview}
                  theme={theme as ThemeType}
                  parentData={data}
                />
                {token ? (
                  <UserNav />
                ) : (
                  <div className='hidden lg:flex lg:gap-x-4'>
                    <button
                      className='flex items-center gap-x-2'
                      onClick={() =>
                        router.push(
                          `/account/signin${
                            tokenPreview ? `?theme_id=${themeId}&token=${tokenPreview}` : ''
                          }`
                        )
                      }
                    >
                      <span className='hidden md:block'>Sign In/Sign Up</span>
                      <SignInIcon className='ml-0 h-7 w-7 hover:cursor-pointer md:ml-2' />
                    </button>
                  </div>
                )}
                <button
                  type='button'
                  onClick={() => setCollapsed((v) => !v)}
                  className={cn('z-40 block lg:hidden')}
                >
                  <Menu className='h-7 w-7' />
                </button>
              </div>
            </div>
            {multiOrigin && (
              <div className='flex flex-col items-center justify-center gap-x-2 pt-3 md:flex-row'>
                <span className='flex items-center gap-x-2'>
                  <MapPin className='h-5 w-5' /> Delivery or Pickup from
                </span>
                <button
                  onClick={() => {
                    modalMultiOrigin.open();
                  }}
                  className='font-bold'
                >
                  {selectedLocation
                    ? selectedLocation.location_name
                    : locationToDisplay?.location_name}
                </button>
              </div>
            )}
          </header>
        </div>
      )}

      <div
        className={cn(
          (headerFixed ||
            ((scrollDirection === 'up' || data.stickyHeader === 'always') && !headerStatic)) &&
            'header-height'
        )}
      ></div>
      {!!data.spacing && <div className='header-spacing'></div>}

      <ModalSearch
        openModal={searchModal.isShow}
        setOpenModal={searchModal.setIsShow}
        host={typeof window !== 'undefined' ? window.location.host : ''}
        preview={!!preview}
      />

      <DrawerSelectLocation
        openModal={modalMultiOrigin.isShow}
        setOpenModal={modalMultiOrigin.setIsShow}
        locations={locationData || []}
        theme={theme as ThemeType}
        preview={preview}
        selectedLocation={selectedLocation}
        setSelectedLocation={setSelectedLocation}
        coordinates={coordinates}
        setCoordinates={setCoordinates}
      />

      <style jsx global>
        {`
          @media (min-width: 768px) {
            .padding-header {
              padding-top: ${data.padding?.top || 0}px;
              padding-bottom: ${data.padding?.bottom || 0}px;
              padding-left: ${(data.styleWidth !== 'box' ? data.padding?.left : preview && 20) ||
              0}px;
              padding-right: ${(data.styleWidth !== 'box' ? data.padding?.right : preview && 20) ||
              0}px;
            }
          }
          .offset-top {
            top: -${headerHeight}px;
          }
          .header-height {
            padding-top: ${headerHeight}px;
          }
          .header-spacing {
            height: ${data.spacing}px;
          }
          .color-scheme-header {
            background-color: ${data?.colorSchemeHeader?.background || '#111827'};
            color: ${data.colorSchemeHeader?.text};
          }
          .header-full-width {
            max-width: unset;
            width: 100%;
          }
          .gap-navigation {
            gap: ${data.spacingNav || 8}px;
          }
        `}
      </style>
    </>
  );
}
